import { useState, useEffect, useCallback } from "react";
import { 
  FolderOpen, Plus, Download, MessageCircle, 
  AlertTriangle, FileText, ChevronDown, ChevronRight,
  Building2, Search, CheckCircle, Circle, Package,
  Clock, StickyNote, History, X, Check, XCircle,
  Eye, Loader2, CheckSquare, Square, MinusSquare,
  Pencil, Save, Trash2, Upload, RefreshCw, Award, Ban
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const axCfg = { withCredentials: true };

// Estados del trámite acordes al proceso CNE
const TRAMITE_STATUS = {
  recabando_documentos: { label: "Recabando Documentos", color: "bg-amber-100 text-amber-800", icon: Clock },
  iniciando_tramite: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: FileText },
  solicitud_enviada: { label: "Solicitud Enviada a Autoridad", color: "bg-purple-100 text-purple-800", icon: Package },
  permiso_aprobado: { label: "Permiso Aprobado", color: "bg-green-100 text-green-800", icon: Award },
  permiso_rechazado: { label: "Permiso Rechazado", color: "bg-red-100 text-red-800", icon: Ban },
  // Legacy states
  documentos_pendientes: { label: "Recabando Documentos", color: "bg-amber-100 text-amber-800", icon: Clock },
  en_revision: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: Eye },
  expediente_completo: { label: "Procesando Información", color: "bg-blue-100 text-blue-800", icon: CheckSquare },
  enviado_cne: { label: "Solicitud Enviada", color: "bg-purple-100 text-purple-800", icon: Package },
  en_proceso_cne: { label: "En Proceso CNE", color: "bg-orange-100 text-orange-800", icon: Clock },
  observaciones_cne: { label: "Observaciones CNE", color: "bg-red-100 text-red-800", icon: AlertTriangle },
  aprobado: { label: "Permiso Aprobado", color: "bg-green-100 text-green-800", icon: Award },
};

const ESTADOS_REQUIEREN_DOCS = ["iniciando_tramite", "solicitud_enviada", "expediente_completo", "enviado_cne", "en_proceso_cne", "aprobado"];

const SERVICE_TYPES = [
  "Permisos de Autoconsumo", 
  "Controles Volumétricos",
  "Gestión Integral", 
  "Suministro de Combustible"
];

const NOTE_TYPES = {
  general: { label: "General", color: "bg-gray-100 text-gray-800" },
  observacion: { label: "Observación", color: "bg-yellow-100 text-yellow-800" },
  urgente: { label: "Urgente", color: "bg-red-100 text-red-800" },
};

export default function TramitesTab() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dataVersion, setDataVersion] = useState(0); // Force re-render on data updates
  const [expandedClient, setExpandedClient] = useState(() => {
    // Restaurar cliente expandido desde localStorage
    return localStorage.getItem('admin_expanded_client') || null;
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [userRole, setUserRole] = useState("admin");
  
  // Obtener rol del usuario
  useEffect(() => {
    axios.get(`${API}/admin/me`, axCfg)
      .then(res => setUserRole(res.data.role || "admin"))
      .catch(() => setUserRole("admin"));
  }, []);
  
  // Guardar cliente expandido en localStorage
  useEffect(() => {
    if (expandedClient) {
      localStorage.setItem('admin_expanded_client', expandedClient);
    }
  }, [expandedClient]);
  
  // Create tramite modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForClient, setCreateForClient] = useState(null);
  const [newTramite, setNewTramite] = useState({ title: "", service_type: "", description: "" });
  
  // Edit tramite modal
  const [editTramiteOpen, setEditTramiteOpen] = useState(false);
  const [editingTramite, setEditingTramite] = useState(null);
  const [editTramiteForm, setEditTramiteForm] = useState({ title: "", description: "" });
  
  // Edit client modal
  const [editClientOpen, setEditClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [editClientForm, setEditClientForm] = useState({ company_name: "", contact_name: "", phone: "" });
  
  // Status update modal
  const [statusOpen, setStatusOpen] = useState(false);
  const [selectedTramite, setSelectedTramite] = useState(null);
  const [statusUpdate, setStatusUpdate] = useState({ status: "", notes: "" });
  
  // Notes modal
  const [notesOpen, setNotesOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newNote, setNewNote] = useState({ content: "", note_type: "general" });
  
  // Document reject modal
  const [rejectOpen, setRejectOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Assign document to requirement modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null); // { tramite, requirementId, requirementName, unassignedDocs }
  const [selectedDocToAssign, setSelectedDocToAssign] = useState("");
  
  // Loading states
  const [validatingDoc, setValidatingDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingTramite, setDeletingTramite] = useState(null);
  
  // Upload/Replace document
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadTarget, setUploadTarget] = useState(null); // { tramite, requirementId, requirementName }
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Replace document modal
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);
  
  // Delete document confirmation modal
  const [deleteDocOpen, setDeleteDocOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  
  // Delete tramite confirmation modal
  const [deleteTramiteOpen, setDeleteTramiteOpen] = useState(false);
  const [tramiteToDelete, setTramiteToDelete] = useState(null);
  
  // Send to authority modal (Enviar a CNE)
  const [sendToAuthorityOpen, setSendToAuthorityOpen] = useState(false);
  const [sendToAuthorityTramite, setSendToAuthorityTramite] = useState(null);
  const [sendToAuthorityStep, setSendToAuthorityStep] = useState(1); // 1: confirm backup, 2: upload acuse
  const [acuseFile, setAcuseFile] = useState(null);
  const [sendingToAuthority, setSendingToAuthority] = useState(false);
  
  // Active tab for tramites view
  const [tramitesViewTab, setTramitesViewTab] = useState("activos"); // "activos" or "enviados_cne"
  
  // Resolution modal (Aprobar/Rechazar permiso)
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [resolutionTramite, setResolutionTramite] = useState(null);
  const [resolutionType, setResolutionType] = useState(""); // "aprobado" | "rechazado"
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [resolutionFile, setResolutionFile] = useState(null);
  const [submittingResolution, setSubmittingResolution] = useState(false);

  // Delete client modal states
  const [deleteClientOpen, setDeleteClientOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deletingClient, setDeletingClient] = useState(false);

  // Delete client function
  const deleteClient = async () => {
    if (!clientToDelete) return;
    setDeletingClient(true);
    try {
      await axios.delete(`${API}/admin/clients/${clientToDelete.id}`, axCfg);
      toast.success("Cliente eliminado correctamente");
      setDeleteClientOpen(false);
      setClientToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar cliente");
    } finally {
      setDeletingClient(false);
    }
  };

  // Delete tramite - open confirmation modal
  const openDeleteTramiteModal = (tramite) => {
    setTramiteToDelete(tramite);
    setDeleteTramiteOpen(true);
  };

  // Confirm delete tramite
  const confirmDeleteTramite = async () => {
    if (!tramiteToDelete) return;
    setDeletingTramite(tramiteToDelete.id);
    try {
      await axios.delete(`${API}/admin/tramites/${tramiteToDelete.id}`, axCfg);
      toast.success("Trámite eliminado correctamente");
      setDeleteTramiteOpen(false);
      setTramiteToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar trámite");
    } finally {
      setDeletingTramite(null);
    }
  };

  // Delete single document
  const deleteDocument = async () => {
    if (!docToDelete) return;
    setDeletingDoc(true);
    try {
      await axios.delete(`${API}/admin/documents/${docToDelete.id}`, axCfg);
      toast.success("Documento eliminado correctamente");
      setDeleteDocOpen(false);
      setDocToDelete(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar documento");
    } finally {
      setDeletingDoc(false);
    }
  };

  // Send tramite to authority (CNE)
  const sendToAuthority = async () => {
    if (!acuseFile || !sendToAuthorityTramite) {
      toast.error("Debes subir el acuse de la solicitud");
      return;
    }
    setSendingToAuthority(true);
    try {
      const formData = new FormData();
      formData.append("acuse_file", acuseFile);
      
      await axios.post(`${API}/admin/tramites/${sendToAuthorityTramite.id}/send-to-authority`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Trámite enviado a autoridad exitosamente");
      setSendToAuthorityOpen(false);
      setSendToAuthorityTramite(null);
      setSendToAuthorityStep(1);
      setAcuseFile(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al enviar a autoridad");
    } finally {
      setSendingToAuthority(false);
    }
  };

  // Upload document from admin
  const uploadDocument = async () => {
    if (!uploadFile || !uploadTarget) {
      toast.error("Selecciona un archivo");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("document_type", uploadTarget.requirementId);
      
      await axios.post(`${API}/admin/tramites/${uploadTarget.tramite.id}/upload`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Documento subido exitosamente");
      setUploadOpen(false);
      setUploadTarget(null);
      setUploadFile(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir documento");
    } finally {
      setUploading(false);
    }
  };

  // Replace existing document
  const replaceDocument = async () => {
    if (!replaceFile || !replaceDoc) {
      toast.error("Selecciona un archivo");
      return;
    }
    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append("file", replaceFile);
      
      await axios.put(`${API}/admin/documents/${replaceDoc.id}/replace`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Documento reemplazado exitosamente");
      setReplaceOpen(false);
      setReplaceDoc(null);
      setReplaceFile(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al reemplazar documento");
    } finally {
      setReplacing(false);
    }
  };

  // Submit resolution (Aprobar/Rechazar permiso)
  const submitResolution = async () => {
    if (!resolutionType) {
      toast.error("Selecciona el tipo de resolución");
      return;
    }
    
    // OBLIGATORIO: Archivo requerido para aprobar o rechazar
    if (!resolutionFile) {
      toast.error(resolutionType === "aprobado" 
        ? "Debes cargar el PDF del permiso aprobado" 
        : "Debes cargar el acuse de rechazo de la CNE");
      return;
    }
    
    setSubmittingResolution(true);
    try {
      const formData = new FormData();
      formData.append("resolution", resolutionType);
      if (resolutionNotes) formData.append("notes", resolutionNotes);
      formData.append("file", resolutionFile);
      
      // Enviar notificación por correo al cliente
      formData.append("send_email", "true");
      
      await axios.post(`${API}/admin/tramites/${resolutionTramite.id}/resolution`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success(resolutionType === "aprobado" 
        ? "¡Permiso aprobado! Se notificará al cliente por correo." 
        : "Permiso rechazado. Se notificará al cliente por correo.");
      setResolutionOpen(false);
      setResolutionTramite(null);
      setResolutionType("");
      setResolutionNotes("");
      setResolutionFile(null);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al procesar resolución");
    } finally {
      setSubmittingResolution(false);
    }
  };

  // Download resolution document
  const downloadResolution = async (tramite) => {
    try {
      const response = await axios.get(`${API}/admin/tramites/${tramite.id}/resolution/download`, {
        ...axCfg,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', tramite.resolution_document?.filename || 'resolucion.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar resolución");
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/clients-with-documents?search=${search}`, axCfg);
      const enriched = await Promise.all(res.data.map(async (client) => {
        const tramitesWithProgress = await Promise.all((client.tramites || []).map(async (t) => {
          try {
            const prog = await axios.get(`${API}/admin/tramites/${t.id}/progress`, axCfg);
            // Deep clone documents to ensure new references
            const documents = (prog.data.documents || []).map(doc => ({...doc}));
            return { ...t, ...prog.data, documents };
          } catch {
            return { ...t, percentage: 0, total_approved: 0, total_required: 0, documents: [] };
          }
        }));
        // Sort tramites by creation date (newest first)
        const sortedTramites = tramitesWithProgress.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        return { ...client, tramites: sortedTramites };
      }));
      // Sort clients alphabetically
      const sortedClients = enriched.sort((a, b) => 
        (a.company_name || "").localeCompare(b.company_name || "", 'es')
      );
      setClients([...sortedClients]); // Create new array reference
      setDataVersion(v => v + 1); // Force re-render
    } catch (e) { 
      toast.error("Error al cargar datos"); 
    }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { 
    const timer = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(timer);
  }, [fetchData]);

  // Auto-refresh cada 10 segundos
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchClientDetails = async (clientId) => {
    try {
      const [notesRes, activityRes] = await Promise.all([
        axios.get(`${API}/admin/clients/${clientId}/notes`, axCfg),
        axios.get(`${API}/admin/clients/${clientId}/activity`, axCfg)
      ]);
      setNotes(notesRes.data);
      setActivity(activityRes.data);
    } catch (e) {
      toast.error("Error al cargar detalles");
    }
  };

  const createTramite = async () => {
    if (!newTramite.title || !newTramite.service_type) {
      toast.error("Completa titulo y tipo de servicio");
      return;
    }
    try {
      await axios.post(`${API}/admin/tramites`, {
        ...newTramite,
        client_id: createForClient.id
      }, axCfg);
      toast.success("Tramite creado");
      setCreateOpen(false);
      setNewTramite({ title: "", service_type: "", description: "" });
      fetchData();
    } catch (e) {
      toast.error("Error al crear tramite");
    }
  };

  // Edit tramite
  const saveTramiteEdit = async () => {
    if (!editTramiteForm.title.trim()) {
      toast.error("El titulo es requerido");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/tramites/${editingTramite.id}`, {
        title: editTramiteForm.title,
        description: editTramiteForm.description
      }, axCfg);
      toast.success("Tramite actualizado");
      setEditTramiteOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  // Edit client
  const saveClientEdit = async () => {
    if (!editClientForm.company_name.trim()) {
      toast.error("El nombre de empresa es requerido");
      return;
    }
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/clients/${editingClient.id}`, {
        company_name: editClientForm.company_name,
        contact_name: editClientForm.contact_name,
        phone: editClientForm.phone
      }, axCfg);
      toast.success("Cliente actualizado");
      setEditClientOpen(false);
      fetchData();
    } catch (e) {
      toast.error("Error al actualizar");
    } finally {
      setSaving(false);
    }
  };

  const updateTramiteStatus = async () => {
    if (!statusUpdate.status) {
      toast.error("Selecciona un estado");
      return;
    }
    try {
      await axios.patch(`${API}/admin/tramites/${selectedTramite.id}/status`, statusUpdate, axCfg);
      toast.success("Estado actualizado");
      setStatusOpen(false);
      setStatusUpdate({ status: "", notes: "" });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al actualizar estado");
    }
  };

  const addNote = async () => {
    if (!newNote.content.trim()) {
      toast.error("Escribe una nota");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("content", newNote.content);
      formData.append("note_type", newNote.note_type);
      await axios.post(`${API}/admin/clients/${selectedClient.id}/notes`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Nota agregada");
      setNewNote({ content: "", note_type: "general" });
      fetchClientDetails(selectedClient.id);
    } catch (e) {
      toast.error("Error al agregar nota");
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm("¿Eliminar esta nota?")) return;
    try {
      await axios.delete(`${API}/admin/clients/${selectedClient.id}/notes/${noteId}`, axCfg);
      toast.success("Nota eliminada");
      fetchClientDetails(selectedClient.id);
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  // Validate document
  const validateDocument = async (doc, newStatus) => {
    setValidatingDoc(doc.id);
    try {
      const formData = new FormData();
      formData.append("validation_status", newStatus);
      formData.append("validation_notes", "");
      await axios.patch(`${API}/admin/documents/${doc.id}/validate`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(newStatus === "approved" ? "✓ Documento aprobado" : newStatus === "rejected" ? "Documento rechazado" : "Estado actualizado");
      fetchData();
    } catch (e) {
      toast.error("Error al validar documento");
    } finally {
      setValidatingDoc(null);
    }
  };

  const rejectDocument = async () => {
    if (!selectedDoc) return;
    setValidatingDoc(selectedDoc.id);
    try {
      const formData = new FormData();
      formData.append("validation_status", "rejected");
      formData.append("validation_notes", rejectReason);
      await axios.patch(`${API}/admin/documents/${selectedDoc.id}/validate`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Documento rechazado");
      setRejectOpen(false);
      setSelectedDoc(null);
      setRejectReason("");
      fetchData();
    } catch (e) {
      toast.error("Error al rechazar documento");
    } finally {
      setValidatingDoc(null);
    }
  };

  // Assign document to requirement
  const assignDocumentToRequirement = async () => {
    if (!selectedDocToAssign || !assignTarget) return;
    try {
      const formData = new FormData();
      formData.append("requirement_id", assignTarget.requirementId);
      await axios.patch(`${API}/admin/documents/${selectedDocToAssign}/assign`, formData, {
        ...axCfg,
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(`Documento asignado a "${assignTarget.requirementName}"`);
      setAssignOpen(false);
      setAssignTarget(null);
      setSelectedDocToAssign("");
      fetchData();
    } catch (e) {
      toast.error("Error al asignar documento");
    }
  };

  // Handle click on empty checkbox to assign document
  const handleEmptyCheckboxClick = (tramite, reqId, reqName, allDocs) => {
    // Get unassigned documents for this tramite
    const reqIds = (tramite.requirements || []).map(r => typeof r === 'object' ? r.id : r);
    const unassigned = allDocs.filter(d => !reqIds.includes(d.document_type));
    
    if (unassigned.length === 0) {
      toast.info("No hay documentos sin clasificar para asignar. El cliente debe subir el documento.");
      return;
    }
    
    setAssignTarget({
      tramite,
      requirementId: reqId,
      requirementName: reqName,
      unassignedDocs: unassigned
    });
    setSelectedDocToAssign("");
    setAssignOpen(true);
  };

  const sendWhatsApp = (client, tramite) => {
    const phone = (client.phone || "").replace(/[^0-9]/g, "");
    if (!phone) {
      toast.error("El cliente no tiene numero de telefono");
      return;
    }
    const missing = tramite.missing || [];
    if (missing.length === 0) {
      toast.info("No hay documentos faltantes");
      return;
    }
    const msg = `Hola ${client.contact_name}, te recordamos que faltan documentos para "${tramite.title}":\n\n${missing.map(d => "• " + d).join("\n")}\n\nPor favor subelos a tu portal de cliente.`;
    window.open(`https://wa.me/52${phone}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const downloadDocument = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${docId}/download`, {
        ...axCfg,
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      toast.success("Descarga iniciada");
    } catch (e) {
      toast.error("Error al descargar");
    }
  };

  const downloadExpediente = async (tramiteId, title, clientName = '', clientRfc = '') => {
    try {
      toast.info("Preparando expediente...");
      const res = await axios.get(`${API}/admin/tramites/${tramiteId}/expediente`, {
        ...axCfg,
        responseType: "blob"
      });
      
      // Intentar obtener el nombre del header Content-Disposition
      const contentDisposition = res.headers['content-disposition'];
      let filename = '';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      // Si no se pudo obtener el nombre del header, construirlo manualmente
      if (!filename || filename === '') {
        const rfc = (clientRfc || '').replace(/\s+/g, '_');
        const name = (clientName || title || tramiteId).replace(/\s+/g, '_');
        filename = `Expediente_${rfc}_${name}.zip`;
      }
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success("Expediente descargado");
    } catch (e) {
      console.error("Error descargando expediente:", e);
      toast.error(e.response?.data?.detail || "Error al descargar expediente");
    }
  };

  const getAvailableStatuses = (tramite) => {
    const isComplete = tramite.percentage === 100;
    return Object.entries(TRAMITE_STATUS).map(([key, value]) => ({
      key,
      ...value,
      disabled: !isComplete && ESTADOS_REQUIEREN_DOCS.includes(key)
    }));
  };

  if (loading) return <p className="text-center py-12 text-gray-500">Cargando...</p>;

  // Filtrar trámites: Activos vs Enviados a CNE (pendientes de resolución) vs Resueltos (aprobados/rechazados)
  const tramitesEnviadosCNE = []; // Solo solicitud_enviada (pendientes de resolución)
  const permisosResueltos = []; // permiso_aprobado + permiso_rechazado
  const clientesConTramitesActivos = [];
  
  clients.forEach(client => {
    // Trámites enviados a CNE pendientes de resolución
    const tramitesPendientesCNE = (client.tramites || []).filter(t => 
      t.status === 'solicitud_enviada'
    );
    // Trámites ya resueltos (aprobados o rechazados)
    const tramitesResueltos = (client.tramites || []).filter(t => 
      t.status === 'permiso_aprobado' || t.status === 'permiso_rechazado'
    );
    // Trámites activos (en proceso interno, no enviados aún)
    const tramitesActivos = (client.tramites || []).filter(t => 
      !['solicitud_enviada', 'permiso_aprobado', 'permiso_rechazado'].includes(t.status)
    );
    
    if (tramitesPendientesCNE.length > 0) {
      tramitesEnviadosCNE.push(...tramitesPendientesCNE.map(t => ({ ...t, client })));
    }
    
    if (tramitesResueltos.length > 0) {
      permisosResueltos.push(...tramitesResueltos.map(t => ({ ...t, client })));
    }
    
    if (tramitesActivos.length > 0) {
      clientesConTramitesActivos.push({
        ...client,
        tramites: tramitesActivos
      });
    }
  });
  
  // Ordenar trámites por fecha
  tramitesEnviadosCNE.sort((a, b) => new Date(b.sent_to_authority_at || b.created_at) - new Date(a.sent_to_authority_at || a.created_at));
  permisosResueltos.sort((a, b) => new Date(b.resolution_date || b.updated_at || b.created_at) - new Date(a.resolution_date || a.updated_at || a.created_at));

  return (
    <div className="space-y-4">
      {/* Tabs for Active vs Sent to CNE vs Resolved */}
      <Tabs value={tramitesViewTab} onValueChange={setTramitesViewTab}>
        <TabsList className="grid w-full max-w-2xl grid-cols-3">
          <TabsTrigger value="activos" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Trámites Activos
            <Badge variant="secondary" className="ml-1">{clientesConTramitesActivos.reduce((acc, c) => acc + (c.tramites?.length || 0), 0)}</Badge>
          </TabsTrigger>
          <TabsTrigger value="enviados_cne" className="flex items-center gap-2">
            <Package className="h-4 w-4" />
            Solicitudes CNE
            <Badge variant="secondary" className="ml-1 bg-purple-100 text-purple-800">{tramitesEnviadosCNE.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="permisos_resueltos" className="flex items-center gap-2">
            <Award className="h-4 w-4" />
            Permisos Aprobados
            <Badge variant="secondary" className="ml-1 bg-green-100 text-green-800">{permisosResueltos.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* Tab: Trámites Activos */}
        <TabsContent value="activos" className="mt-4">
      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar trámite..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <span className="text-sm text-gray-500">
          {clientesConTramitesActivos.reduce((acc, c) => acc + (c.tramites?.length || 0), 0)} trámites
        </span>
      </div>

      {/* Clients */}
      {clientesConTramitesActivos.length === 0 ? (
        <Card className="border-none">
          <CardContent className="text-center py-16">
            <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay clientes con trámites activos</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {clientesConTramitesActivos.map((client) => {
            // Check if any tramite needs attention (all docs uploaded but still in recabando_documentos - pending review)
            const hasNeedsReview = (client.tramites || []).some(t => 
              t.all_uploaded && (t.status === 'recabando_documentos' || t.status === 'documentos_pendientes')
            );
            // Check if any tramite has been processed (already in iniciando_tramite or higher)
            const hasProcessedExpediente = (client.tramites || []).some(t => 
              t.status === 'iniciando_tramite' || t.status === 'expediente_completo'
            );
            
            // Determine visual state: yellow for needs review, green for processed
            const visualState = hasNeedsReview ? 'needs_review' : (hasProcessedExpediente ? 'processed' : 'normal');
            
            return (
            <Card key={client.id} className={`border-none shadow-sm ${
              visualState === 'needs_review' ? 'ring-2 ring-amber-400' :
              visualState === 'processed' ? 'ring-2 ring-green-400' : ''
            }`}>
              <CardContent className="p-0">
                {/* Client header */}
                <div 
                  className={`p-3 flex items-center gap-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                    visualState === 'needs_review' ? 'bg-amber-50' :
                    visualState === 'processed' ? 'bg-green-50' : ''
                  }`}
                  onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                >
                  <button className="text-gray-400">
                    {expandedClient === client.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  </button>
                  
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    visualState === 'needs_review' ? 'bg-amber-100' :
                    visualState === 'processed' ? 'bg-green-100' : 'bg-[#103F38]/10'
                  }`}>
                    {visualState === 'needs_review' 
                      ? <CheckCircle className="h-5 w-5 text-amber-600" />
                      : visualState === 'processed'
                        ? <CheckCircle className="h-5 w-5 text-green-600" />
                        : <Building2 className="h-4 w-4 text-[#103F38]" />
                    }
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${
                        visualState === 'needs_review' ? 'text-amber-800' :
                        visualState === 'processed' ? 'text-green-800' : 'text-gray-900'
                      }`}>
                        {client.company_name}
                      </span>
                      {visualState === 'needs_review' && (
                        <Badge className="bg-amber-100 text-amber-800 text-xs animate-pulse">
                          ⚠️ Requiere Revisión
                        </Badge>
                      )}
                      {visualState === 'processed' && (
                        <Badge className="bg-green-100 text-green-800 text-xs">
                          Expediente Completo
                        </Badge>
                      )}
                      <span className="text-xs text-gray-500 font-mono">{client.rfc}</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-6 w-6 p-0 text-gray-400 hover:text-[#103F38]"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingClient(client);
                          setEditClientForm({
                            company_name: client.company_name || "",
                            contact_name: client.contact_name || "",
                            phone: client.phone || ""
                          });
                          setEditClientOpen(true);
                        }}
                        title="Editar cliente"
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    </div>
                    <div className="text-xs text-gray-500">
                      {client.contact_name} • {client.tramites?.length || 0} tramites
                    </div>
                  </div>
                  
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-gray-500 h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClient(client);
                        fetchClientDetails(client.id);
                        setNotesOpen(true);
                      }}
                      title="Notas e historial"
                    >
                      <StickyNote className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-[#103F38] h-8"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCreateForClient(client);
                        setCreateOpen(true);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Tramite
                    </Button>
                  </div>
                </div>

                {/* Expanded tramites */}
                {expandedClient === client.id && (
                  <div className="border-t bg-gray-50/50 p-4">
                    {client.tramites?.length > 0 ? (
                      <div className="space-y-4">
                        {client.tramites.map((tramite) => {
                          const statusCfg = TRAMITE_STATUS[tramite.status] || TRAMITE_STATUS.documentos_pendientes;
                          const StatusIcon = statusCfg.icon || Clock;
                          const progress = tramite.percentage || 0;
                          const allDocs = tramite.documents || [];
                          const hasMissing = (tramite.missing?.length || 0) > 0;
                          const isComplete = progress === 100;
                          
                          // Check if tramite needs review (all docs uploaded but still in recabando_documentos)
                          const needsReview = tramite.all_uploaded && (tramite.status === 'recabando_documentos' || tramite.status === 'documentos_pendientes');
                          // Check if tramite is processed (already moved to iniciando_tramite or higher)
                          const isProcessed = tramite.status === 'iniciando_tramite' || tramite.status === 'expediente_completo';
                          
                          return (
                            <div key={tramite.id} className={`rounded-lg border p-4 ${
                              needsReview ? 'bg-amber-50 border-amber-400 border-2' :
                              isProcessed || isComplete ? 'bg-green-50 border-green-300' : 'bg-white'
                            }`}>
                              {/* Header */}
                              <div className="flex items-start justify-between mb-4">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {/* Icono de check amarillo cuando requiere revisión */}
                                    {needsReview && (
                                      <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center animate-pulse">
                                        <Check className="h-4 w-4 text-white" />
                                      </div>
                                    )}
                                    <span className={`font-semibold ${needsReview ? 'text-amber-900' : 'text-gray-900'}`}>{tramite.title}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-gray-400 hover:text-[#103F38]"
                                      onClick={() => {
                                        setEditingTramite(tramite);
                                        setEditTramiteForm({
                                          title: tramite.title || "",
                                          description: tramite.description || ""
                                        });
                                        setEditTramiteOpen(true);
                                      }}
                                      title="Editar tramite"
                                    >
                                      <Pencil className="h-3 w-3" />
                                    </Button>
                                    {/* Botón eliminar - solo admin */}
                                    {userRole === "admin" && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-7 px-2 text-red-400 hover:text-red-600 hover:bg-red-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openDeleteTramiteModal(tramite);
                                        }}
                                        disabled={deletingTramite === tramite.id}
                                        title="Eliminar tramite"
                                        data-testid={`delete-tramite-${tramite.id}`}
                                      >
                                        {deletingTramite === tramite.id ? (
                                          <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : (
                                          <>
                                            <Trash2 className="h-4 w-4 mr-1" />
                                            <span className="text-xs">Eliminar</span>
                                          </>
                                        )}
                                      </Button>
                                    )}
                                    <Badge className={statusCfg.color}>
                                      <StatusIcon className="h-3 w-3 mr-1" />
                                      {statusCfg.label}
                                    </Badge>
                                    {needsReview && (
                                      <Badge className="bg-amber-400 text-amber-900 text-xs animate-pulse font-semibold">
                                        ⚠️ REQUIERE REVISIÓN
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500 mt-1">{tramite.service_type}</p>
                                </div>
                                
                                <div className="text-center ml-4">
                                  <div className={`text-3xl font-bold ${
                                    needsReview ? 'text-amber-600' :
                                    isComplete || isProcessed ? 'text-green-600' : 
                                    progress >= 50 ? 'text-amber-600' : 'text-gray-500'
                                  }`}>
                                    {progress}%
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {tramite.total_approved || 0}/{tramite.total_required || 0}
                                  </p>
                                </div>
                              </div>
                              
                              <Progress value={progress} className={`h-2 mb-4 ${
                                needsReview ? '[&>div]:bg-amber-500' :
                                isComplete || isProcessed ? '[&>div]:bg-green-600' : ''
                              }`} />
                              
                              {/* Documents checklist */}
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="text-xs font-semibold text-gray-700 uppercase">
                                    Checklist de Documentos
                                  </p>
                                </div>
                                <div className="border rounded-lg divide-y bg-white">
                                  {tramite.requirements?.map((req, idx) => {
                                    const reqId = typeof req === 'object' ? req.id : req;
                                    const reqName = typeof req === 'object' ? req.nombre : req;
                                    
                                    // Find document for this requirement
                                    const doc = allDocs.find(d => d.document_type === reqId);
                                    const docStatus = doc?.validation_status || null;
                                    const isValidating = validatingDoc === doc?.id;
                                    
                                    return (
                                      <div 
                                        key={`${reqId}-${doc?.id || 'empty'}-${docStatus}-${dataVersion}`}
                                        className={`p-3 flex items-center gap-3 ${
                                          docStatus === 'approved' ? 'bg-green-50' :
                                          docStatus === 'rejected' ? 'bg-red-50' :
                                          doc ? 'bg-yellow-50' : 'bg-white'
                                        }`}
                                      >
                                        {/* Checkbox cuadrado */}
                                        {doc ? (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              if (docStatus === 'approved') {
                                                validateDocument(doc, 'pending');
                                              } else {
                                                validateDocument(doc, 'approved');
                                              }
                                            }}
                                            disabled={isValidating}
                                            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#103F38] rounded"
                                          >
                                            {isValidating ? (
                                              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                            ) : docStatus === 'approved' ? (
                                              <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center cursor-pointer hover:bg-green-700">
                                                <Check className="h-4 w-4 text-white" />
                                              </div>
                                            ) : docStatus === 'rejected' ? (
                                              <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center cursor-pointer hover:bg-red-700">
                                                <X className="h-4 w-4 text-white" />
                                              </div>
                                            ) : (
                                              <div className="w-6 h-6 border-2 border-yellow-500 rounded bg-white cursor-pointer hover:border-green-500 hover:bg-green-50" />
                                            )}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              handleEmptyCheckboxClick(tramite, reqId, reqName, allDocs);
                                            }}
                                            className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#103F38] rounded"
                                            title="Clic para asignar documento"
                                          >
                                            <div className="w-6 h-6 border-2 border-gray-300 rounded bg-gray-100 cursor-pointer hover:border-[#103F38] hover:bg-[#103F38]/10 transition-colors" />
                                          </button>
                                        )}
                                        
                                        {/* Requirement info */}
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium ${
                                            docStatus === 'approved' ? 'text-green-800' :
                                            docStatus === 'rejected' ? 'text-red-800' :
                                            doc ? 'text-yellow-800' : 'text-gray-600'
                                          }`}>
                                            {reqName}
                                          </p>
                                          
                                          {/* Show uploaded file info */}
                                          {doc && (
                                            <div className="flex items-center gap-2 mt-1">
                                              <FileText className="h-3 w-3 text-gray-400" />
                                              <span className="text-xs text-gray-500 truncate max-w-xs">
                                                {doc.original_filename}
                                              </span>
                                              <span className="text-xs text-gray-400">
                                                ({new Date(doc.uploaded_at).toLocaleDateString('es-MX')})
                                              </span>
                                            </div>
                                          )}
                                          
                                          {/* Show rejection reason */}
                                          {docStatus === 'rejected' && doc.validation_notes && (
                                            <p className="text-xs text-red-600 mt-1">
                                              Motivo: {doc.validation_notes}
                                            </p>
                                          )}
                                        </div>
                                        
                                        {/* Actions */}
                                        {doc && (
                                          <div className="flex items-center gap-1 flex-shrink-0">
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs"
                                              onClick={() => downloadDocument(doc.id, doc.original_filename)}
                                              title="Descargar"
                                            >
                                              <Download className="h-3 w-3" />
                                            </Button>
                                            
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs text-blue-600 border-blue-300 hover:bg-blue-50"
                                              onClick={() => {
                                                setReplaceDoc(doc);
                                                setReplaceFile(null);
                                                setReplaceOpen(true);
                                              }}
                                              title="Reemplazar archivo"
                                            >
                                              <RefreshCw className="h-3 w-3" />
                                            </Button>
                                            
                                            {docStatus !== 'rejected' && (
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                                                onClick={() => {
                                                  setSelectedDoc(doc);
                                                  setRejectOpen(true);
                                                }}
                                                title="Rechazar"
                                              >
                                                <XCircle className="h-3 w-3" />
                                              </Button>
                                            )}
                                            
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs text-red-600 border-red-300 hover:bg-red-50"
                                              onClick={() => {
                                                setDocToDelete(doc);
                                                setDeleteDocOpen(true);
                                              }}
                                              title="Eliminar archivo"
                                            >
                                              <Trash2 className="h-3 w-3" />
                                            </Button>
                                          </div>
                                        )}
                                        
                                        {!doc && (
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className="text-xs text-gray-400">Sin documento</span>
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              className="h-7 text-xs text-[#103F38] border-[#103F38]/50 hover:bg-[#103F38]/10"
                                              onClick={() => {
                                                setUploadTarget({ tramite, requirementId: reqId, requirementName: reqName });
                                                setUploadFile(null);
                                                setUploadOpen(true);
                                              }}
                                              title="Subir archivo desde PC"
                                            >
                                              <Upload className="h-3 w-3 mr-1" />
                                              Subir
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                              
                              {/* Unassigned documents */}
                              {(() => {
                                const reqIds = (tramite.requirements || []).map(r => typeof r === 'object' ? r.id : r);
                                const unassigned = allDocs.filter(d => !reqIds.includes(d.document_type));
                                
                                if (unassigned.length === 0) return null;
                                
                                return (
                                  <div className="mb-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
                                    <p className="text-xs font-semibold text-orange-800 uppercase mb-2">
                                      Documentos Subidos (Sin Clasificar: {unassigned.length})
                                    </p>
                                    <div className="space-y-2">
                                      {unassigned.map((doc) => {
                                        const docStatus = doc.validation_status || 'pending';
                                        const isValidating = validatingDoc === doc.id;
                                        
                                        return (
                                          <div 
                                            key={doc.id}
                                            className={`p-2 rounded flex items-center gap-3 ${
                                              docStatus === 'approved' ? 'bg-green-100' :
                                              docStatus === 'rejected' ? 'bg-red-100' : 'bg-yellow-100'
                                            }`}
                                          >
                                            {/* Checkbox cuadrado */}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (docStatus === 'approved') {
                                                  validateDocument(doc, 'pending');
                                                } else {
                                                  validateDocument(doc, 'approved');
                                                }
                                              }}
                                              disabled={isValidating}
                                              className="flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-[#103F38] rounded"
                                            >
                                              {isValidating ? (
                                                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                                              ) : docStatus === 'approved' ? (
                                                <div className="w-6 h-6 bg-green-600 rounded flex items-center justify-center cursor-pointer hover:bg-green-700">
                                                  <Check className="h-4 w-4 text-white" />
                                                </div>
                                              ) : docStatus === 'rejected' ? (
                                                <div className="w-6 h-6 bg-red-600 rounded flex items-center justify-center cursor-pointer hover:bg-red-700">
                                                  <X className="h-4 w-4 text-white" />
                                                </div>
                                              ) : (
                                                <div className="w-6 h-6 border-2 border-yellow-500 rounded bg-white cursor-pointer hover:border-green-500 hover:bg-green-50" />
                                              )}
                                            </button>
                                            
                                            <div className="flex-1 min-w-0">
                                              <p className="text-sm font-medium text-gray-800 truncate">
                                                {doc.original_filename}
                                              </p>
                                              <p className="text-xs text-gray-600">
                                                Tipo: {doc.document_type} • {new Date(doc.uploaded_at).toLocaleDateString('es-MX')}
                                              </p>
                                            </div>
                                            
                                            <div className="flex gap-1 flex-shrink-0">
                                              <Button
                                                size="sm"
                                                variant="outline"
                                                className="h-7 text-xs"
                                                onClick={() => downloadDocument(doc.id, doc.original_filename)}
                                              >
                                                <Download className="h-3 w-3" />
                                              </Button>
                                              {docStatus !== 'rejected' && (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  className="h-7 text-xs text-red-600 border-red-300"
                                                  onClick={() => {
                                                    setSelectedDoc(doc);
                                                    setRejectOpen(true);
                                                  }}
                                                >
                                                  <XCircle className="h-3 w-3" />
                                                </Button>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Actions */}
                              <div className="flex gap-2 pt-3 border-t flex-wrap">
                                {hasMissing && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 border-green-300"
                                    onClick={() => sendWhatsApp(client, tramite)}
                                  >
                                    <MessageCircle className="h-3 w-3 mr-1" />
                                    WhatsApp: Recordar
                                  </Button>
                                )}
                                {allDocs.length > 0 && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-[#103F38]"
                                    onClick={() => downloadExpediente(tramite.id, tramite.title, client.company_name, client.rfc)}
                                  >
                                    <Package className="h-3 w-3 mr-1" />
                                    Descargar Expediente
                                  </Button>
                                )}
                                {isComplete && (
                                  <Badge className="bg-green-100 text-green-800 ml-auto">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Expediente completo
                                  </Badge>
                                )}
                                
                                {/* Button to send to authority - show when expediente is complete (iniciando_tramite) */}
                                {(tramite.status === 'iniciando_tramite' || (tramite.status === 'en_revision' && isComplete) || (tramite.status === 'expediente_completo')) && (
                                  <Button
                                    size="sm"
                                    className="bg-purple-600 hover:bg-purple-700 text-white ml-auto"
                                    onClick={() => {
                                      setSendToAuthorityTramite({
                                        ...tramite,
                                        client_name: client.company_name,
                                        client_rfc: client.rfc
                                      });
                                      setSendToAuthorityStep(1);
                                      setAcuseFile(null);
                                      setSendToAuthorityOpen(true);
                                    }}
                                  >
                                    <Package className="h-3 w-3 mr-1" />
                                    Enviar a Autoridad
                                  </Button>
                                )}
                                
                                {/* Show resolution document download if exists */}
                                {(tramite.status === 'permiso_aprobado' || tramite.status === 'permiso_rechazado' || tramite.status === 'aprobado') && tramite.resolution_document && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className={tramite.status === 'permiso_rechazado' ? "text-red-600 border-red-300 ml-auto" : "text-green-600 border-green-300 ml-auto"}
                                    onClick={() => downloadResolution(tramite)}
                                  >
                                    <Download className="h-3 w-3 mr-1" />
                                    Ver Resolución
                                  </Button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 text-center py-4">Sin tramites</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
          })}
        </div>
      )}
        </TabsContent>

        {/* Tab: Solicitudes Enviadas a CNE */}
        <TabsContent value="enviados_cne" className="mt-4">
          {tramitesEnviadosCNE.length === 0 ? (
            <Card className="border-none">
              <CardContent className="text-center py-16">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay solicitudes enviadas a la CNE</p>
                <p className="text-xs text-gray-400 mt-2">Las solicitudes aparecerán aquí cuando se envíen a la autoridad</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {tramitesEnviadosCNE.map((tramite) => {
                const statusCfg = TRAMITE_STATUS[tramite.status] || TRAMITE_STATUS.solicitud_enviada;
                const StatusIcon = statusCfg.icon;
                const daysRemaining = tramite.sent_to_authority_at 
                  ? Math.max(0, 90 - Math.floor((Date.now() - new Date(tramite.sent_to_authority_at).getTime()) / (1000 * 60 * 60 * 24)))
                  : 90;
                
                return (
                  <Card key={tramite.id} className="border-none shadow-sm">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            tramite.status === 'permiso_aprobado' ? 'bg-green-100' :
                            tramite.status === 'permiso_rechazado' ? 'bg-red-100' :
                            'bg-purple-100'
                          }`}>
                            <StatusIcon className={`h-5 w-5 ${
                              tramite.status === 'permiso_aprobado' ? 'text-green-600' :
                              tramite.status === 'permiso_rechazado' ? 'text-red-600' :
                              'text-purple-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900">{tramite.title}</h3>
                            <p className="text-sm text-gray-500">
                              {tramite.client?.company_name} • {tramite.service_type}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                              {tramite.status === 'solicitud_enviada' && (
                                <Badge variant="outline" className="text-xs">
                                  <Clock className="h-3 w-3 mr-1" />
                                  {daysRemaining} días restantes
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Botón ver acuse - Solo para admins */}
                          {tramite.acuse_document && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-purple-600 border-purple-300"
                              onClick={async () => {
                                try {
                                  const res = await axios.get(`${API}/admin/tramites/${tramite.id}/acuse/download`, {
                                    ...axCfg,
                                    responseType: 'blob'
                                  });
                                  const url = window.URL.createObjectURL(new Blob([res.data]));
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = tramite.acuse_document.original_filename || 'acuse_solicitud.pdf';
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                  window.URL.revokeObjectURL(url);
                                } catch (e) {
                                  toast.error("Error al descargar acuse");
                                }
                              }}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              Ver Acuse
                            </Button>
                          )}
                          
                          {/* Botones Aprobar/Rechazar solo para solicitud_enviada */}
                          {tramite.status === 'solicitud_enviada' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => {
                                  setResolutionTramite({
                                    ...tramite,
                                    client_email: tramite.client?.email
                                  });
                                  setResolutionType('aprobado');
                                  setResolutionNotes('');
                                  setResolutionFile(null);
                                  setResolutionOpen(true);
                                }}
                              >
                                <Award className="h-4 w-4 mr-1" />
                                Aprobar Permiso
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => {
                                  setResolutionTramite({
                                    ...tramite,
                                    client_email: tramite.client?.email
                                  });
                                  setResolutionType('rechazado');
                                  setResolutionNotes('');
                                  setResolutionFile(null);
                                  setResolutionOpen(true);
                                }}
                              >
                                <Ban className="h-4 w-4 mr-1" />
                                Rechazar Permiso
                              </Button>
                            </>
                          )}
                          
                          {/* Ver documento de resolución si ya está aprobado/rechazado */}
                          {(tramite.status === 'permiso_aprobado' || tramite.status === 'permiso_rechazado') && tramite.resolution_document && (
                            <Button
                              size="sm"
                              variant="outline"
                              className={tramite.status === 'permiso_rechazado' ? "text-red-600 border-red-300" : "text-green-600 border-green-300"}
                              onClick={() => downloadResolution(tramite)}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              {tramite.status === 'permiso_aprobado' ? 'Ver Permiso' : 'Ver Rechazo'}
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Info adicional */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <span>Enviado: {tramite.sent_to_authority_at ? new Date(tramite.sent_to_authority_at).toLocaleDateString('es-MX') : 'N/A'}</span>
                        <span>ID: {tramite.id.substring(0, 8)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tab: Permisos Aprobados/Rechazados */}
        <TabsContent value="permisos_resueltos" className="mt-4">
          {permisosResueltos.length === 0 ? (
            <Card className="border-none">
              <CardContent className="text-center py-16">
                <Award className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No hay permisos resueltos todavía</p>
                <p className="text-xs text-gray-400 mt-2">Los permisos aprobados o rechazados aparecerán aquí</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {/* Estadísticas rápidas */}
              <div className="flex gap-4 mb-4">
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-lg border border-green-200">
                  <Award className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-medium text-green-800">
                    {permisosResueltos.filter(t => t.status === 'permiso_aprobado').length} Aprobados
                  </span>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-red-50 rounded-lg border border-red-200">
                  <Ban className="h-5 w-5 text-red-600" />
                  <span className="text-sm font-medium text-red-800">
                    {permisosResueltos.filter(t => t.status === 'permiso_rechazado').length} Rechazados
                  </span>
                </div>
              </div>

              {permisosResueltos.map((tramite) => {
                const isApproved = tramite.status === 'permiso_aprobado';
                const statusCfg = TRAMITE_STATUS[tramite.status] || TRAMITE_STATUS.permiso_aprobado;
                const StatusIcon = statusCfg.icon;
                
                return (
                  <Card 
                    key={tramite.id} 
                    className={`border-none shadow-sm ${isApproved ? 'bg-green-50/50 border-l-4 border-l-green-500' : 'bg-red-50/50 border-l-4 border-l-red-500'}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                            isApproved ? 'bg-green-100' : 'bg-red-100'
                          }`}>
                            <StatusIcon className={`h-6 w-6 ${
                              isApproved ? 'text-green-600' : 'text-red-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{tramite.title}</h3>
                            <p className="text-sm text-gray-600">
                              {tramite.client?.company_name} • {tramite.service_type}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge className={statusCfg.color}>
                                {isApproved ? '✓ APROBADO' : '✗ RECHAZADO'}
                              </Badge>
                              {tramite.resolution_date && (
                                <span className="text-xs text-gray-500">
                                  Resuelto: {new Date(tramite.resolution_date).toLocaleDateString('es-MX', { 
                                    day: '2-digit', month: 'short', year: 'numeric' 
                                  })}
                                </span>
                              )}
                            </div>
                            {tramite.resolution_notes && (
                              <p className="text-xs text-gray-500 mt-1 italic">
                                "{tramite.resolution_notes}"
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Descargar documento de resolución */}
                          {tramite.resolution_document && (
                            <Button
                              size="sm"
                              className={isApproved 
                                ? "bg-green-600 hover:bg-green-700 text-white" 
                                : "bg-red-600 hover:bg-red-700 text-white"
                              }
                              onClick={() => downloadResolution(tramite)}
                            >
                              <Download className="h-4 w-4 mr-1" />
                              {isApproved ? 'Descargar Permiso' : 'Descargar Acuse'}
                            </Button>
                          )}
                          
                          {/* Descargar expediente completo */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => downloadExpediente(tramite.id, tramite.title, tramite.client?.company_name, tramite.client?.rfc)}
                          >
                            <FolderOpen className="h-4 w-4 mr-1" />
                            Expediente
                          </Button>
                          
                          {/* Ver detalles del cliente */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => {
                              setSelectedClient(tramite.client);
                              fetchClientDetails(tramite.client?.id);
                              setNotesOpen(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Timeline resumido */}
                      <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-500">
                        <div className="flex items-center gap-4">
                          <span>📋 Creado: {new Date(tramite.created_at).toLocaleDateString('es-MX')}</span>
                          {tramite.sent_to_authority_at && (
                            <span>📤 Enviado: {new Date(tramite.sent_to_authority_at).toLocaleDateString('es-MX')}</span>
                          )}
                          <span>{isApproved ? '✅' : '❌'} Resuelto: {tramite.resolution_date ? new Date(tramite.resolution_date).toLocaleDateString('es-MX') : 'N/A'}</span>
                        </div>
                        <span className="font-mono">ID: {tramite.id.substring(0, 8)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Create Tramite Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo Tramite para {createForClient?.company_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titulo *</Label>
              <Input
                value={newTramite.title}
                onChange={(e) => setNewTramite({...newTramite, title: e.target.value})}
                placeholder="Ej: Permiso Autoconsumo 2024"
              />
            </div>
            <div>
              <Label>Tipo de Servicio *</Label>
              <Select 
                value={newTramite.service_type} 
                onValueChange={(v) => setNewTramite({...newTramite, service_type: v})}
              >
                <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                <SelectContent>
                  {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={newTramite.description}
                onChange={(e) => setNewTramite({...newTramite, description: e.target.value})}
              />
            </div>
            <Button onClick={createTramite} className="w-full bg-[#103F38]">
              Crear Tramite
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tramite Dialog */}
      <Dialog open={editTramiteOpen} onOpenChange={setEditTramiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tramite</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Titulo *</Label>
              <Input
                value={editTramiteForm.title}
                onChange={(e) => setEditTramiteForm({...editTramiteForm, title: e.target.value})}
              />
            </div>
            <div>
              <Label>Descripcion</Label>
              <Textarea
                value={editTramiteForm.description}
                onChange={(e) => setEditTramiteForm({...editTramiteForm, description: e.target.value})}
              />
            </div>
            <Button onClick={saveTramiteEdit} disabled={saving} className="w-full bg-[#103F38]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editClientOpen} onOpenChange={setEditClientOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre de Empresa *</Label>
              <Input
                value={editClientForm.company_name}
                onChange={(e) => setEditClientForm({...editClientForm, company_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Nombre de Contacto</Label>
              <Input
                value={editClientForm.contact_name}
                onChange={(e) => setEditClientForm({...editClientForm, contact_name: e.target.value})}
              />
            </div>
            <div>
              <Label>Telefono</Label>
              <Input
                value={editClientForm.phone}
                onChange={(e) => setEditClientForm({...editClientForm, phone: e.target.value})}
              />
            </div>
            <Button onClick={saveClientEdit} disabled={saving} className="w-full bg-[#103F38]">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
              Guardar Cambios
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Estado: {selectedTramite?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTramite && selectedTramite.percentage < 100 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                Expediente incompleto ({selectedTramite.percentage}%). Algunos estados no están disponibles.
              </div>
            )}
            <Select 
              value={statusUpdate.status} 
              onValueChange={(v) => setStatusUpdate({...statusUpdate, status: v})}
            >
              <SelectTrigger><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
              <SelectContent>
                {selectedTramite && getAvailableStatuses(selectedTramite).map((status) => (
                  <SelectItem 
                    key={status.key} 
                    value={status.key}
                    disabled={status.disabled}
                  >
                    {status.label} {status.disabled && "(requiere 100%)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              placeholder="Notas..."
              value={statusUpdate.notes}
              onChange={(e) => setStatusUpdate({...statusUpdate, notes: e.target.value})}
            />
            <Button onClick={updateTramiteStatus} className="w-full bg-[#103F38]">
              Actualizar Estado
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject Document Dialog */}
      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedDoc && (
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="font-medium text-sm">{selectedDoc.original_filename}</p>
                <p className="text-xs text-gray-500">Tipo: {selectedDoc.document_type}</p>
              </div>
            )}
            <div>
              <Label>Motivo del rechazo *</Label>
              <Textarea
                placeholder="Describe por qué se rechaza este documento..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRejectOpen(false)} className="flex-1">
                Cancelar
              </Button>
              <Button 
                onClick={rejectDocument}
                disabled={!rejectReason.trim() || validatingDoc}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {validatingDoc ? <Loader2 className="h-4 w-4 animate-spin" /> : "Rechazar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Notes Dialog */}
      <Dialog open={notesOpen} onOpenChange={setNotesOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>{selectedClient?.company_name} - Notas e Historial</DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="notes" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="notes">Notas ({notes.length})</TabsTrigger>
              <TabsTrigger value="activity">Historial ({activity.length})</TabsTrigger>
            </TabsList>
            
            <TabsContent value="notes" className="flex-1 overflow-hidden flex flex-col mt-4">
              <div className="space-y-2 mb-4">
                <Textarea
                  placeholder="Escribe una nota..."
                  value={newNote.content}
                  onChange={(e) => setNewNote({...newNote, content: e.target.value})}
                  rows={2}
                />
                <div className="flex gap-2 justify-between">
                  <Select value={newNote.note_type} onValueChange={(v) => setNewNote({...newNote, note_type: v})}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="observacion">Observacion</SelectItem>
                      <SelectItem value="urgente">Urgente</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={addNote} className="bg-[#103F38]">Agregar</Button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto space-y-2">
                {notes.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Sin notas</p>
                ) : notes.map((note) => (
                  <div key={note.id} className="bg-gray-50 rounded-lg p-3 group">
                    <div className="flex justify-between">
                      <div>
                        <Badge className={NOTE_TYPES[note.note_type]?.color + " text-xs mb-1"}>
                          {NOTE_TYPES[note.note_type]?.label}
                        </Badge>
                        <p className="text-sm">{note.content}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(note.created_at).toLocaleString('es-MX')}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0 text-red-500"
                        onClick={() => deleteNote(note.id)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
            
            <TabsContent value="activity" className="flex-1 overflow-y-auto mt-4">
              {activity.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin actividad</p>
              ) : (
                <div className="space-y-2">
                  {activity.map((act) => (
                    <div key={act.id} className="flex gap-3 p-2 border-l-2 border-[#103F38]/30">
                      <Clock className="h-4 w-4 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium">{act.action}</p>
                        {act.details && <p className="text-xs text-gray-600">{act.details}</p>}
                        <p className="text-xs text-gray-400">{new Date(act.timestamp).toLocaleString('es-MX')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Assign Document to Requirement Dialog */}
      <Dialog open={assignOpen} onOpenChange={(open) => {
        setAssignOpen(open);
        if (!open) {
          setAssignTarget(null);
          setSelectedDocToAssign("");
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Asignar Documento a Requisito</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {assignTarget && (
              <>
                <div className="bg-[#103F38]/10 rounded-lg p-3">
                  <p className="text-sm font-medium text-[#103F38]">Requisito:</p>
                  <p className="text-sm">{assignTarget.requirementName}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Selecciona el documento a asignar</Label>
                  <p className="text-xs text-gray-500 mb-2">
                    Documentos sin clasificar disponibles: {assignTarget.unassignedDocs?.length || 0}
                  </p>
                  <Select value={selectedDocToAssign} onValueChange={setSelectedDocToAssign}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona un documento" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignTarget.unassignedDocs?.map((doc) => (
                        <SelectItem key={doc.id} value={doc.id}>
                          <div className="flex items-center gap-2">
                            <FileText className="h-3 w-3" />
                            <span className="truncate max-w-[250px]">{doc.original_filename}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setAssignOpen(false)} 
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={assignDocumentToRequirement}
                    disabled={!selectedDocToAssign}
                    className="flex-1 bg-[#103F38] hover:bg-[#143f34]"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    Asignar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Document Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(open) => {
        setUploadOpen(open);
        if (!open) {
          setUploadTarget(null);
          setUploadFile(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Subir Documento desde PC</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {uploadTarget && (
              <>
                <div className="bg-[#103F38]/10 rounded-lg p-3">
                  <p className="text-sm font-medium text-[#103F38]">Requisito:</p>
                  <p className="text-sm">{uploadTarget.requirementName}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Selecciona el archivo</Label>
                  <Input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className="mt-2"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  />
                  {uploadFile && (
                    <p className="text-xs text-gray-500 mt-1">
                      Archivo seleccionado: {uploadFile.name}
                    </p>
                  )}
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setUploadOpen(false)} 
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={uploadDocument}
                    disabled={!uploadFile || uploading}
                    className="flex-1 bg-[#103F38] hover:bg-[#143f34]"
                  >
                    {uploading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Subir Documento
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Replace Document Dialog */}
      <Dialog open={replaceOpen} onOpenChange={(open) => {
        setReplaceOpen(open);
        if (!open) {
          setReplaceDoc(null);
          setReplaceFile(null);
        }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reemplazar Documento</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {replaceDoc && (
              <>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-yellow-800">Documento actual:</p>
                  <p className="text-sm text-yellow-700">{replaceDoc.original_filename}</p>
                  <p className="text-xs text-yellow-600 mt-1">
                    Tipo: {replaceDoc.document_type}
                  </p>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Selecciona el nuevo archivo</Label>
                  <Input
                    type="file"
                    onChange={(e) => setReplaceFile(e.target.files?.[0] || null)}
                    className="mt-2"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  />
                  {replaceFile && (
                    <p className="text-xs text-green-600 mt-1">
                      Nuevo archivo: {replaceFile.name}
                    </p>
                  )}
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2">
                  <p className="text-xs text-amber-700">
                    ⚠️ El archivo actual será reemplazado y el estado de validación se reiniciará a "Pendiente".
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setReplaceOpen(false)} 
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={replaceDocument}
                    disabled={!replaceFile || replacing}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {replacing ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    Reemplazar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Resolution Dialog (Aprobar/Rechazar Permiso) */}
      <Dialog open={resolutionOpen} onOpenChange={(open) => {
        setResolutionOpen(open);
        if (!open) {
          setResolutionTramite(null);
          setResolutionType("");
          setResolutionNotes("");
          setResolutionFile(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {resolutionType === "aprobado" ? (
                <>
                  <Award className="h-5 w-5 text-green-600" />
                  Aprobar Permiso
                </>
              ) : (
                <>
                  <Ban className="h-5 w-5 text-red-600" />
                  Rechazar Permiso
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {resolutionTramite && (
              <>
                <div className={`rounded-lg p-4 ${resolutionType === "aprobado" ? "bg-green-50 border border-green-200" : "bg-red-50 border border-red-200"}`}>
                  <p className="font-medium text-sm">{resolutionTramite.title}</p>
                  <p className="text-xs text-gray-600 mt-1">Cliente: {resolutionTramite.client?.company_name || resolutionTramite.client_name}</p>
                  {resolutionTramite.client?.email && (
                    <p className="text-xs text-gray-500 mt-1">📧 Se notificará a: {resolutionTramite.client.email}</p>
                  )}
                </div>

                <div className={`rounded-lg p-3 border-2 border-dashed ${
                  resolutionType === "aprobado" ? "border-green-400 bg-green-50" : "border-red-400 bg-red-50"
                }`}>
                  <Label className={`text-sm font-bold ${resolutionType === "aprobado" ? "text-green-800" : "text-red-800"}`}>
                    {resolutionType === "aprobado" 
                      ? "📄 Cargar PDF del Permiso Aprobado *" 
                      : "📄 Cargar Acuse de Rechazo de la CNE *"}
                  </Label>
                  <p className={`text-xs mt-1 mb-2 ${resolutionType === "aprobado" ? "text-green-700" : "text-red-700"}`}>
                    <strong>OBLIGATORIO:</strong> Sin este archivo no podrás continuar
                  </p>
                  <Input
                    type="file"
                    onChange={(e) => setResolutionFile(e.target.files?.[0] || null)}
                    className="bg-white"
                    accept=".pdf"
                  />
                  {resolutionFile && (
                    <p className="text-xs text-green-700 mt-2 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Archivo seleccionado: {resolutionFile.name}
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-sm font-medium">Notas (opcional)</Label>
                  <Textarea
                    value={resolutionNotes}
                    onChange={(e) => setResolutionNotes(e.target.value)}
                    placeholder={resolutionType === "aprobado" 
                      ? "Ej: Permiso otorgado por la CNE con folio XXX-2026" 
                      : "Ej: Rechazado por documentación incompleta, favor de revisar observaciones..."}
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className={`rounded-lg p-3 ${resolutionType === "aprobado" ? "bg-green-100 border border-green-300" : "bg-red-100 border border-red-300"}`}>
                  <p className={`text-sm ${resolutionType === "aprobado" ? "text-green-800" : "text-red-800"}`}>
                    {resolutionType === "aprobado" 
                      ? "✓ El trámite se marcará como 'Permiso Aprobado' y el cliente podrá descargar el permiso desde su portal."
                      : "⚠️ El trámite se marcará como 'Permiso Rechazado' y el cliente podrá descargar el acuse de rechazo."
                    }
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setResolutionOpen(false)} 
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={submitResolution}
                    disabled={submittingResolution}
                    className={`flex-1 ${resolutionType === "aprobado" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                  >
                    {submittingResolution ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : resolutionType === "aprobado" ? (
                      <Award className="h-4 w-4 mr-2" />
                    ) : (
                      <Ban className="h-4 w-4 mr-2" />
                    )}
                    {resolutionType === "aprobado" ? "Confirmar Aprobación" : "Confirmar Rechazo"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Document Confirmation Dialog */}
      <Dialog open={deleteDocOpen} onOpenChange={(open) => {
        setDeleteDocOpen(open);
        if (!open) {
          setDocToDelete(null);
        }
      }}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Documento
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {docToDelete && (
              <>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800">
                    ¿Estás seguro de querer eliminar el archivo cargado por cliente?
                  </p>
                  <div className="mt-3 p-2 bg-white rounded border border-red-100">
                    <p className="text-sm text-gray-700 font-medium">{docToDelete.original_filename}</p>
                    <p className="text-xs text-gray-500 mt-1">Tipo: {docToDelete.document_type}</p>
                  </div>
                  <p className="text-xs text-red-600 mt-3">
                    ⚠️ Esta acción no se puede deshacer. El cliente deberá subir el documento nuevamente.
                  </p>
                </div>

                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setDeleteDocOpen(false)} 
                    className="flex-1"
                  >
                    Cancelar
                  </Button>
                  <Button 
                    onClick={deleteDocument}
                    disabled={deletingDoc}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                  >
                    {deletingDoc ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Trash2 className="h-4 w-4 mr-2" />
                    )}
                    Sí, Eliminar
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Authority Dialog */}
      <Dialog open={sendToAuthorityOpen} onOpenChange={(open) => {
        setSendToAuthorityOpen(open);
        if (!open) {
          setSendToAuthorityTramite(null);
          setSendToAuthorityStep(1);
          setAcuseFile(null);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              Enviar Solicitud a CNE
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {sendToAuthorityTramite && (
              <>
                {/* Step 1: Confirm backup */}
                {sendToAuthorityStep === 1 && (
                  <>
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800">¿Se respaldó expediente de cliente?</p>
                          <p className="text-sm text-amber-700 mt-1">
                            Antes de enviar la solicitud a la CNE, asegúrate de tener un respaldo del expediente completo.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm font-medium">{sendToAuthorityTramite.title}</p>
                      <p className="text-xs text-gray-500 mt-1">{sendToAuthorityTramite.client_name || sendToAuthorityTramite.client?.company_name}</p>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          downloadExpediente(
                            sendToAuthorityTramite.id, 
                            sendToAuthorityTramite.title,
                            sendToAuthorityTramite.client_name || sendToAuthorityTramite.client?.company_name || '',
                            sendToAuthorityTramite.client_rfc || sendToAuthorityTramite.client?.rfc || ''
                          );
                        }}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Descargar Expediente
                      </Button>
                      <Button 
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => setSendToAuthorityStep(2)}
                      >
                        Sí, Continuar
                      </Button>
                    </div>
                  </>
                )}

                {/* Step 2: Upload Acuse */}
                {sendToAuthorityStep === 2 && (
                  <>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                      <p className="font-medium text-purple-800">Cargar Acuse de Solicitud</p>
                      <p className="text-sm text-purple-700 mt-1">
                        Es <strong>OBLIGATORIO</strong> cargar el PDF con el acuse de la solicitud del trámite ante la Comisión Nacional de Energía para continuar.
                      </p>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Acuse de Solicitud (PDF) *</Label>
                      <Input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setAcuseFile(e.target.files?.[0] || null)}
                        className="mt-2"
                      />
                      {acuseFile && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" />
                          Archivo seleccionado: {acuseFile.name}
                        </p>
                      )}
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <p className="text-sm text-blue-800">
                        ℹ️ Al enviar, el trámite cambiará a estado <strong>"Solicitud Enviada a Autoridad"</strong> y comenzará el conteo de <strong>90 días</strong> para la respuesta de la CNE.
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        onClick={() => setSendToAuthorityStep(1)} 
                        className="flex-1"
                      >
                        Atrás
                      </Button>
                      <Button 
                        onClick={sendToAuthority}
                        disabled={!acuseFile || sendingToAuthority}
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                      >
                        {sendingToAuthority ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="h-4 w-4 mr-2" />
                        )}
                        Enviar a CNE
                      </Button>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Tramite Confirmation Modal */}
      <Dialog open={deleteTramiteOpen} onOpenChange={setDeleteTramiteOpen}>
        <DialogContent className="sm:max-w-[450px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Trámite
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer
            </DialogDescription>
          </DialogHeader>

          {tramiteToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800 font-medium mb-2">
                  ¿Estás seguro de eliminar el siguiente trámite?
                </p>
                <div className="bg-white rounded p-3 border border-red-100">
                  <p className="font-semibold text-gray-900">{tramiteToDelete.title}</p>
                  <p className="text-sm text-gray-600">{tramiteToDelete.service_type}</p>
                  {tramiteToDelete.client?.company_name && (
                    <p className="text-sm text-gray-500">Cliente: {tramiteToDelete.client.company_name}</p>
                  )}
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>⚠️ Advertencia:</strong> Al eliminar este trámite también se eliminarán:
                </p>
                <ul className="list-disc ml-5 mt-1 text-sm text-amber-700">
                  <li>Todos los documentos asociados</li>
                  <li>El historial de estados</li>
                  <li>Los archivos subidos</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteTramiteOpen(false);
                setTramiteToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteTramite}
              disabled={deletingTramite}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingTramite ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sí, Eliminar Trámite
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Client Confirmation Modal */}
      <Dialog open={deleteClientOpen} onOpenChange={setDeleteClientOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Eliminar Cliente
            </DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Se eliminarán permanentemente:
            </DialogDescription>
          </DialogHeader>

          {clientToDelete && (
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <p className="font-semibold text-gray-900">{clientToDelete.company_name}</p>
                <p className="text-sm text-gray-600">RFC: {clientToDelete.rfc || "N/A"}</p>
                <p className="text-sm text-gray-500">Contacto: {clientToDelete.contact_name}</p>
                <p className="text-sm text-gray-500">{clientToDelete.email}</p>
              </div>
              
              <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                <p className="text-sm font-medium text-red-800 mb-2">Se eliminarán:</p>
                <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Todos sus trámites ({clientToDelete.tramites?.length || 0})</li>
                  <li>Todos los documentos asociados</li>
                  <li>Todo el historial del cliente</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setDeleteClientOpen(false);
                setClientToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={deleteClient}
              disabled={deletingClient}
              className="bg-red-600 hover:bg-red-700"
            >
              {deletingClient ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Eliminando...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Sí, Eliminar Cliente
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
